import { FormikErrors } from 'formik';

import { StepId } from '@/components/hook/applicationSteps';
import { ApplicationFormValues } from './applicationValidation';

/** One entry in the submit-time error summary. */
export type ApplicationErrorItem = {
  /** Human label shown in the summary list. */
  label: string;
  /** CSS selector for the field the link scrolls/focuses. */
  selector: string;
};

type FieldMeta = {
  field: keyof ApplicationFormValues;
  label: string;
  selector: string;
};

const INFO_META: FieldMeta[] = [
  {
    field: 'firstName',
    label: 'First name',
    selector: '[data-testid="signup-first-name"]'
  },
  {
    field: 'lastName',
    label: 'Last name',
    selector: '[data-testid="signup-last-name"]'
  },
  { field: 'email', label: 'Email', selector: '[data-testid="signup-email"]' },
  {
    field: 'phone',
    label: 'Phone number',
    selector: '[data-testid="signup-phone"]'
  },
  {
    field: 'password',
    label: 'Password',
    selector: '[data-testid="signup-password"]'
  },
  {
    field: 'birthday',
    label: 'Birthday',
    selector: '[data-testid="signup-birthday"]'
  }
];

const SHIPPING_META: FieldMeta[] = [
  {
    field: 'address',
    label: 'Shipping address',
    selector: '.ApplicationShippingFields .Signup-address input'
  },
  {
    field: 'city',
    label: 'Shipping city',
    selector: 'input[name="city"]'
  },
  {
    field: 'state',
    label: 'Shipping state',
    selector: 'select[name="state"]'
  },
  {
    field: 'postalCode',
    label: 'Shipping postal code',
    selector: '#postalCode'
  }
];

const BILLING_META: FieldMeta[] = [
  {
    field: 'billingAddress',
    label: 'Billing address',
    selector: '.Signup-billing-address .Signup-address input'
  },
  {
    field: 'billingCity',
    label: 'Billing city',
    selector: 'input[name="billingCity"]'
  },
  {
    field: 'billingState',
    label: 'Billing state',
    selector: 'select[name="billingState"]'
  },
  {
    field: 'billingPostalCode',
    label: 'Billing postal code',
    selector: '#billingPostalCode'
  }
];

const PAYMENT_ITEM: ApplicationErrorItem = {
  label: 'Payment method',
  selector: '[data-testid="payment-fields"]'
};

const TERMS_ITEM: ApplicationErrorItem = {
  label: 'Terms and conditions',
  selector: '[name="terms"]'
};

const PAYMENT_AGREEMENT_ITEM: ApplicationErrorItem = {
  label: 'Membership agreement',
  selector: '[name="paymentAgreement"]'
};

/**
 * True when the only thing left is the consent checkboxes. They sit right
 * above the submit button, so a one-line message (the multi-page flow's own
 * copy) reads better than the full summary box.
 */
export function isConsentOnly(items: ApplicationErrorItem[]): boolean {
  return (
    items.length > 0 &&
    items.every(
      (item) => item === TERMS_ITEM || item === PAYMENT_AGREEMENT_ITEM
    )
  );
}

function toItems(
  meta: FieldMeta[],
  errors: FormikErrors<ApplicationFormValues>
): ApplicationErrorItem[] {
  return meta
    .filter((m) => !!errors[m.field])
    .map(({ label, selector }) => ({ label, selector }));
}

/**
 * Builds the submit-time error summary in **section order**: info, then
 * shipping, then whatever is left — the payment method and the two consents.
 * The summary points at the first section with a problem, which is the one
 * that gets expanded.
 *
 * Returns `step: null` with no items when nothing is invalid — the signal to
 * let the submit proceed.
 */
export function buildErrorItems(
  errors: FormikErrors<ApplicationFormValues>,
  values: ApplicationFormValues,
  paymentComplete: boolean
): { step: StepId | null; items: ApplicationErrorItem[] } {
  const infoItems = toItems(INFO_META, errors);
  if (infoItems.length > 0) {
    return { step: 'info', items: infoItems };
  }

  const shippingItems = toItems(SHIPPING_META, errors);

  if (shippingItems.length > 0) {
    return { step: 'shipping', items: shippingItems };
  }

  // The billing address, when the member said it differs, belongs to the same
  // section as the card — so both are reported against `billing`.
  const billingItems = values.sameAsShippingAddress
    ? []
    : toItems(BILLING_META, errors);

  if (billingItems.length > 0 || !paymentComplete) {
    return {
      step: 'billing',
      items: paymentComplete ? billingItems : [...billingItems, PAYMENT_ITEM]
    };
  }

  const finalItems: ApplicationErrorItem[] = [];
  if (!values.paymentAgreement) {
    finalItems.push(PAYMENT_AGREEMENT_ITEM);
  }
  if (!values.terms) {
    finalItems.push(TERMS_ITEM);
  }

  // `step: null` here means "nothing to expand" — these controls are always
  // visible below the steps, not inside a collapsible one.
  return { step: null, items: finalItems };
}
